import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type SelectedDetail =
  | { type: 'trader'; userId: string; username?: string; orgId?: string }
  | { type: 'org'; orgId: string; orgName?: string }
  | null;

interface DetailNavValue {
  selected: SelectedDetail;
  selectTrader: (args: { userId: string; username?: string; orgId?: string }) => void;
  selectOrg: (args: { orgId: string; orgName?: string }) => void;
  clear: () => void;
}

const DetailNavContext = createContext<DetailNavValue | null>(null);

export function DetailNavProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<SelectedDetail>(null);

  const selectTrader = useCallback(
    (args: { userId: string; username?: string; orgId?: string }) => {
      setSelected({ type: 'trader', ...args });
    },
    [],
  );

  const selectOrg = useCallback(
    (args: { orgId: string; orgName?: string }) => {
      setSelected({ type: 'org', ...args });
    },
    [],
  );

  const clear = useCallback(() => setSelected(null), []);

  const value = useMemo(
    () => ({ selected, selectTrader, selectOrg, clear }),
    [selected, selectTrader, selectOrg, clear],
  );

  return <DetailNavContext.Provider value={value}>{children}</DetailNavContext.Provider>;
}

export function useDetailNav(): DetailNavValue {
  const ctx = useContext(DetailNavContext);
  if (!ctx) {
    throw new Error('useDetailNav must be used inside DetailNavProvider');
  }
  return ctx;
}
